<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import { watchEffect } from 'vue';
import { BaseSearchFilterDto, CustomMetadataSearchFilterElementDto, EntityCustomMetadataDto } from '@/api';
import { getProviderIconSrc } from '@/utils/provider-icons';
import { VTreeview } from 'vuetify/lib';
import SAvatars from '@/components/SAvatars/SAvatars.vue';
import SEllipsisTooltip from '@/components/SEllipsisTooltip/SEllipsisTooltip.vue';
import CreationMethodIcon from '@/components/icons/CreationMethodIcon.vue';
import AssetTypeIcon from '@/components/icons/Asset-Type-Icon.vue';
import PlatformIcon from '@/components/icons/Platform-Icon.vue';
import IngestionTypeIcon from '@/components/icons/Ingestion-Type-Icon.vue';
import QualityStatus from '@/components/quality-status/Quality-Status.vue';
import RunStatus from '@/modules/monitors/monitor/common/RunStatus.vue';
import DataUsage from '@/components/usage/Usage.vue';
import Tag from '@/components/tags/Tag.vue';
import Term from '@/components/terms/Term.vue';
import Severity from '@/components/Severity.vue';
import IncidentStatus from '@/components/incidents/Incident-Status.vue';
import MonitorMutingStatus from '@/components/monitors/Monitor-Muting-Status.vue';

interface SearchFilterTreeviewSelectorProps {
  type: string;
  items: { name: string;[key: string]: any }[];
  selectedItems: string[];
  lazyLoading: boolean,
  search: string;
}

const props =  defineProps({
  type: null,
  items: null,
  selectedItems: null,
  lazyLoading: { type: Boolean },
  search: null
});

type SearchFilterTreeviewSelectorEmit = {
  (event: 'updateSelectedItems', items: string[]): void;
  (event: 'updateLazyLoadingState', isLoading: boolean): void;
}
const emits = defineEmits(["updateSelectedItems", "updateLazyLoadingState"]);

const treeviewRef = _ref<typeof VTreeview | null>(null);

const type = _computed(() => props.type);
const items = _computed(() => props.items);
const lazyLoading = _computed(() => props.lazyLoading);
const selectedItems = _computed({
  get: () => props.selectedItems,
  set: (val: string[]) => {
    emits('updateSelectedItems', val);
  },
});
const search = _computed(() => props.search);

const isAssetType = _computed(() => type.value === BaseSearchFilterDto.type.ASSET_TYPE);
const isPlatform = _computed(() => type.value === BaseSearchFilterDto.type.PLATFORM);
const isTemplate = _computed(() => type.value === BaseSearchFilterDto.type.RULE_TEMPLATE_NAME);
const isHealth = _computed(() => type.value === BaseSearchFilterDto.type.HEALTH_STATUS);
const isUsage = _computed(() => type.value === BaseSearchFilterDto.type.USAGE);
const isTag = _computed(() => type.value === BaseSearchFilterDto.type.TAG);
const isTerm = _computed(() => type.value === BaseSearchFilterDto.type.TERM);
const isCriticality = _computed(() => type.value === BaseSearchFilterDto.type.CRITICALITY);
const isOwner = _computed(() => type.value === BaseSearchFilterDto.type.OWNER);
const isCreatedBy = _computed(() => type.value === BaseSearchFilterDto.type.CREATED_BY);
const isTechnology = _computed(() => type.value === BaseSearchFilterDto.type.TECHNOLOGY);
const isAssetIngestion = _computed(() => type.value === BaseSearchFilterDto.type.ASSET_INGESTION);
const isCreationMethod = _computed(() => type.value === BaseSearchFilterDto.type.CREATION_METHOD);
const isRuleStatus = _computed(() => type.value === BaseSearchFilterDto.type.RULE_STATUS);
const isCustomMetadata = _computed(() => type.value === BaseSearchFilterDto.type.CUSTOM_METADATA);
const isIncidentStatus = _computed(() => type.value === BaseSearchFilterDto.type.INCIDENT_STATUS);
const isMonitorMutingStatus = _computed(() => type.value === BaseSearchFilterDto.type.MONITOR_MUTING_STATUS);
const isTeam = _computed(() => type.value === BaseSearchFilterDto.type.TEAM);
const isDataProduct = _computed(() => type.value === BaseSearchFilterDto.type.DATA_PRODUCT);

// if item has even one children property with lenght > 0
const hasChildren = _computed(() => items.value?.some((item: any) => 'children' in item && item.children?.length));
const wrapperClass = _computed(() => (hasChildren.value ? 'v-treeview--has-children' : 'v-treeview--has-no-children'));
const loading = (item: any) => item.results === null;

const isExternalCatalogMetadata = (customMetadata: CustomMetadataSearchFilterElementDto) => customMetadata.lineagePlatform != null && customMetadata.lineagePlatform !== CustomMetadataSearchFilterElementDto.lineagePlatform.SIFFLET;

// Used to track when rendering of lazy loaded element ends
watchEffect(() => {
  if (treeviewRef.value && lazyLoading.value) {
    emits('updateLazyLoadingState', false);
  }
});
</script>

<template lang="pug">
div( :class="wrapperClass" )
  v-treeview(
    v-bind='$attrs'
    v-on='$listeners'
    v-model="selectedItems"
    :items="items"
    :search="search"
    selected-color="primary"
    selection-type="leaf"
    ref="treeviewRef"
    data-track="tree-selector-treeview"
    transition
    selectable
    style="max-height: 200px; overflow-y: auto;")
    template( v-slot:label="{ item }" )
      .d-flex.align-center( :class="{ leaf: item.leaf }" )

        template( v-if="isAssetType" )
          AssetTypeIcon.ml-1( :value="item" )

        template( v-if="isPlatform" )
          PlatformIcon.ml-1( :value="item" )

        template( v-if="isTechnology" )
          img.mx-1( :src="getProviderIconSrc(item.icon)" width="20" :alt="item.name")
          span {{ item.name }}

        template( v-if="isAssetIngestion" )
          IngestionTypeIcon.mx-1( :ingestion-id="item.id")
          span.ml-1 {{ item.label }}

        template( v-if="isTemplate" )
          span.ml-1 {{ item.name }}

        template( v-if="isHealth" )
          QualityStatus.ml-1( :value="item.id" )

        template( v-if="isRuleStatus" )
          RunStatus( :status="item.id" ).ml-1

        template( v-if="isOwner || isCreatedBy" )
          SAvatars.ml-1( :users="[item]" size="20" )

        template( v-if="isUsage")
          DataUsage.ml-1( :value="item.id" )

        template( v-if="isTag" )
          Tag.ml-1( :tag="item" small filter)

        template( v-if="isTerm" )
          Term.ml-1( :term="item" small )

        template( v-if="isCriticality" )
          Severity.ml-1( :value="item.id" isFilter)

        template( v-if="isIncidentStatus" )
          IncidentStatus.ml-1( :status="item.id" )

        template( v-if="isMonitorMutingStatus" )
          MonitorMutingStatus.ml-1( :status="item.id" )

        template( v-if="isCreationMethod" )
          CreationMethodIcon.ml-1( :value="item" )

        template( v-if="isTeam" )
          SAvatars.ml-1( :users="[{ ...item, type: 'TEAM' }]" size="20" )

        template( v-if="isDataProduct" )
          SEllipsisTooltip.ml-1( :content="item.name" )

        template( v-if="isCustomMetadata" )
          template( v-if="item.customMetadataType === EntityCustomMetadataDto.type.USER" )
            SAvatars.ml-1( :users="[item]" size="20" )

          template( v-else-if="item.customMetadataType === EntityCustomMetadataDto.type.TEAM" )
            SAvatars.ml-1( :users="[{ ...item, type: 'TEAM' }]" size="20" )

          template( v-else-if="item.customMetadataType === EntityCustomMetadataDto.type.STRING" )
            span(v-if="isExternalCatalogMetadata(item)")
              SIcon.mx-1.mt-1(  color="iconNeutral" :icon="getProviderIconSrc(`${item.lineagePlatform}_LOCK`)" :alt="item.lineagePlatform" size="small" )
            SEllipsisTooltip.ml-1( :content="item.value" )

          template( v-else-if="item.customMetadataType === EntityCustomMetadataDto.type.LABEL" )
            span(v-if="isExternalCatalogMetadata(item)")
              SIcon.mx-1.mt-1(color="iconNeutral" :icon="getProviderIconSrc(`${item.lineagePlatform}_LOCK`)" :alt="item.lineagePlatform" size="small" )
            SEllipsisTooltip.ml-1( :content="item.value" )

        .item-results.d-flex.ml-auto
          span.caption.grey--text( class="font-weight-medium" v-if="loading(item)")
            v-progress-circular.mr-2.ml-1(indeterminate size="15")
          span.caption.grey--text.ml-1(v-else) {{ item.results || '' }}
</template>

<style lang="scss" scoped>
.v-treeview--has-no-children {
  margin-left: -22px;
}

.v-treeview *:not(.item-results) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
